import { useState } from "react";
import {
  Form,
  Input,
  Radio,
  Select,
  Switch,
  DatePicker,
  Upload,
  Button,
  message,
  Space,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  SendOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import usePermission from "../../hooks/usePermission";
import { listRiders } from "../../services/riders.service";
import { createBroadcast } from "../../services/broadcast.service";

const { TextArea } = Input;

const TARGET_OPTIONS = [
  { label: "All Riders", value: "all" },
  { label: "By Project", value: "project" },
  { label: "By Zone", value: "zone" },
  { label: "By City", value: "city" },
  { label: "Select Individuals", value: "individuals" },
];

const MAX_ATTACHMENT_MB = 5;

export default function CreateBroadcastTab({ onSent }) {
  const { canEdit } = usePermission("communications");
  const [form] = Form.useForm();
  const [targetType, setTargetType] = useState("all");
  const [schedule, setSchedule] = useState("now");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [riderOptions, setRiderOptions] = useState([]);
  const [riderSearching, setRiderSearching] = useState(false);

  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: zoneOptions } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "zone",
  });
  const { options: cityOptions } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "city",
  });

  const handleRiderSearch = async (value) => {
    if (!value || value.trim().length < 2) {
      setRiderOptions([]);
      return;
    }
    setRiderSearching(true);
    try {
      const res = await listRiders({ search: value, per_page: 20, status: "active" });
      const records = res?.data?.records || [];
      setRiderOptions(
        records.map((r) => ({
          label: `${r.fullName} (${r.riderId})`,
          value: r._id,
        }))
      );
    } catch {
      setRiderOptions([]);
    } finally {
      setRiderSearching(false);
    }
  };

  const beforeUpload = (candidateFile) => {
    const isAllowed = ["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(
      candidateFile.type
    );
    if (!isAllowed) {
      message.error("Only PDF, PNG, or JPG files are allowed");
      return Upload.LIST_IGNORE;
    }
    if (candidateFile.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      message.error(`File must be ${MAX_ATTACHMENT_MB} MB or less`);
      return Upload.LIST_IGNORE;
    }
    setFile(candidateFile);
    return false;
  };

  const resetForm = () => {
    form.resetFields();
    setTargetType("all");
    setSchedule("now");
    setFile(null);
    setRiderOptions([]);
  };

  const handleSubmit = async (values) => {
    let targetIds = [];
    if (values.targetType === "project") targetIds = values.projectIds || [];
    else if (values.targetType === "zone") targetIds = values.zoneIds || [];
    else if (values.targetType === "city") targetIds = values.cityIds || [];
    else if (values.targetType === "individuals") targetIds = values.riderIds || [];

    if (values.targetType !== "all" && targetIds.length === 0) {
      message.error("Select at least one target for the chosen audience");
      return;
    }

    const payload = {
      title: values.title,
      body: values.body,
      targetType: values.targetType,
      targetIds,
      priority: values.urgent ? "urgent" : "normal",
      sendNow: schedule === "now",
    };
    if (schedule === "later" && values.scheduledAt) {
      payload.scheduledAt = values.scheduledAt.toISOString();
    }

    setSubmitting(true);
    try {
      await createBroadcast(payload, file);
      message.success(
        schedule === "now" ? "Broadcast sent successfully" : "Broadcast scheduled successfully"
      );
      resetForm();
      onSent?.();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Failed to send broadcast");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: 10,
        padding: 24,
        maxWidth: 720,
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ targetType: "all", urgent: false }}
        disabled={!canEdit}
      >
        <Form.Item
          name="title"
          label="Message Title"
          rules={[
            { required: true, message: "Title is required" },
            { max: 100, message: "Title must be 100 characters or less" },
          ]}
        >
          <Input placeholder="e.g. Holiday Schedule Update" maxLength={100} showCount />
        </Form.Item>

        <Form.Item
          name="body"
          label="Message Body"
          extra="Tip: wrap text in **double asterisks** for bold, start a line with '- ' for a bullet point."
          rules={[
            { required: true, message: "Message body is required" },
            { max: 5000, message: "Message body must be 5000 characters or less" },
          ]}
        >
          <TextArea rows={6} placeholder="Write your announcement..." maxLength={5000} showCount />
        </Form.Item>

        <Form.Item name="targetType" label="Target Audience" rules={[{ required: true }]}>
          <Radio.Group
            options={TARGET_OPTIONS}
            onChange={(e) => setTargetType(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          />
        </Form.Item>

        {targetType === "project" && (
          <Form.Item name="projectIds" label="Select Project(s)">
            <Select
              mode="multiple"
              placeholder="Select one or more projects"
              options={projectOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        {targetType === "zone" && (
          <Form.Item name="zoneIds" label="Select Zone(s)">
            <Select
              mode="multiple"
              placeholder="Select one or more zones"
              options={zoneOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        {targetType === "city" && (
          <Form.Item name="cityIds" label="Select City(s)">
            <Select
              mode="multiple"
              placeholder="Select one or more cities"
              options={cityOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        {targetType === "individuals" && (
          <Form.Item name="riderIds" label="Select Rider(s)">
            <Select
              mode="multiple"
              placeholder="Type at least 2 characters to search by name or ID"
              options={riderOptions}
              filterOption={false}
              onSearch={handleRiderSearch}
              loading={riderSearching}
              notFoundContent={riderSearching ? "Searching..." : "No matches"}
              showSearch
            />
          </Form.Item>
        )}

        <Form.Item label="Attachment (optional, max 5 MB)">
          {file ? (
            <Space>
              <PaperClipOutlined />
              <span>{file.name}</span>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setFile(null)}
              />
            </Space>
          ) : (
            <Upload accept=".pdf,.png,.jpg,.jpeg" showUploadList={false} beforeUpload={beforeUpload}>
              <Button icon={<UploadOutlined />}>Attach File</Button>
            </Upload>
          )}
        </Form.Item>

        <Form.Item label="Schedule">
          <Radio.Group value={schedule} onChange={(e) => setSchedule(e.target.value)}>
            <Radio.Button value="now">Send Now</Radio.Button>
            <Radio.Button value="later">Schedule for Later</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {schedule === "later" && (
          <Form.Item
            name="scheduledAt"
            label="Scheduled Date & Time"
            rules={[{ required: true, message: "Pick a future date and time" }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              disabledDate={(d) => d && d < dayjs().startOf("day")}
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}

        <Form.Item name="urgent" label="Priority" valuePropName="checked">
          <Switch checkedChildren="Urgent" unCheckedChildren="Normal" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={submitting}
            style={{ background: "#111827", borderColor: "#111827" }}
            disabled={!canEdit}
          >
            {schedule === "now" ? "Send Broadcast" : "Schedule Broadcast"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
